/**
 * Parent-origin key-value bridge (incident 4b READ + incident 4c WRITE).
 *
 * The standalone tool pages (My Bows / My Arrows / Tune / Spine / Range)
 * run in an iframe whose real origin is forge.sparrowexpeditions.com — a
 * THIRD-PARTY origin under the storefront. Browsers partition or
 * ephemeralize third-party storage (Safari, and especially iOS Home Screen
 * web apps, wipe it), which is how customers lost gear they saved DIRECTLY
 * on the tool pages: writes worked, reloads erased them (incident 4c, Cody).
 *
 * The storefront page hosts durable FIRST-PARTY localStorage
 * (sparrowexpeditions.com — shared by every embed on the page) and answers
 * two postMessage verbs, keys namespaced under the 'spxkv.' prefix:
 *   - `sparrowForge.kvWrite { key, value }`         (mirror a value in)
 *   - `sparrowForge.kvRead  { reqId, keys } -> kvResult { values }`
 *
 * READ (incident 4b): tool pages read the main Forge's mirrored library
 * (`sparrow_setups_v2` + `sparrow_arrow_builds`) to surface it for import.
 *
 * WRITE (incident 4c): tool pages now ALSO mirror their OWN native saves
 * (`sparrowForge.bowProfiles.v1` / `arrowProfiles.v1`) into that same
 * first-party store, and rehydrate from it on boot — so bows/arrows saved
 * here survive a third-party storage wipe exactly like the main Forge's.
 *
 * Fail-open: if the hosting section has no kv handler (older theme, or the
 * patch not yet published), reads time out to {} and writes are silently
 * dropped — the tool pages behave exactly as before.
 */
import { isTrustedTierOrigin } from '@/hooks/useSubscription';

let seq = 0;

/**
 * Fire-and-forget mirror of a key/value into the parent storefront's
 * first-party storage. The parent handler whitelists the 'sparrow' key
 * prefix, so only `sparrow*` keys are accepted (our profile keys qualify).
 * No-op when there is no parent (standalone dev / sandboxed).
 */
export function mirrorToParent(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'sparrowForge.kvWrite', key, value }, '*');
    }
  } catch {
    /* sandboxed / no parent — local persistence still applies */
  }
}

/**
 * Ask the parent storefront for stored values by key. Resolves {} on timeout
 * (old theme section, standalone dev, sandboxed) so callers treat the result
 * as optional. Keys are the RAW app keys (e.g. 'sparrow_setups_v2'); the
 * theme handler maps them to its 'spxkv.' prefix internally.
 */
export function requestParentValues(
  keys: string[],
  timeoutMs = 3000,
): Promise<Record<string, string | null>> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.parent || window.parent === window) {
      resolve({});
      return;
    }
    const reqId = `kv-${Date.now()}-${(seq += 1)}`;
    const onMsg = (e: MessageEvent) => {
      if (!isTrustedTierOrigin(e.origin)) return;
      const d = e.data as
        | { type?: string; reqId?: string; values?: Record<string, string | null> }
        | null;
      if (!d || d.type !== 'sparrowForge.kvResult' || d.reqId !== reqId) return;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMsg);
      resolve(d.values ?? {});
    };
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMsg);
      resolve({});
    }, timeoutMs);
    window.addEventListener('message', onMsg);
    try {
      window.parent.postMessage({ type: 'sparrowForge.kvRead', reqId, keys }, '*');
    } catch {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMsg);
      resolve({});
    }
  });
}

/** Storefront-mirror keys the main Forge writes (must match the main app). */
export const FORGE_SETUPS_KEY = 'sparrow_setups_v2';
export const FORGE_SAVED_BUILDS_KEY = 'sparrow_arrow_builds';
